/* eslint-disable react/no-did-update-set-state */
import * as React from 'react';
import classnames from 'classnames';

import { getElementInfo } from 'utils/element';

import './collapsible-content.scss';

import type { TransitionEvent } from 'react';

type Props = {
  isExpanded?: boolean,
  children?: React.ReactNode,
  wrapperClassName?: string,
  contentClassName?: string,
  skipInitialAnimation?: boolean,
  heightOverride?: number | undefined | null
};

type State = {
  isExpanded: boolean,
  contentHeight: number | undefined | null,
  unsetContentHeight: boolean,
  skipInitialAnimation: boolean
};

class CollapsibleContainer extends React.PureComponent<Props, State> {
  state = {
    isExpanded: this.props.isExpanded || false,
    contentHeight: this.props.isExpanded ? undefined : 0,
    unsetContentHeight: false,
    skipInitialAnimation: this.props.skipInitialAnimation ?? false
  };

  timeoutId: number | undefined;

  contentRef: React.RefObject<any> = React.createRef();

  componentDidMount() {
    const { skipInitialAnimation, heightOverride } = this.props;

    if (!skipInitialAnimation) {
      const contentInfo = getElementInfo(this.contentRef.current);
      const initialHeight = contentInfo?.height || heightOverride || 0;

      this.setState({
        contentHeight: initialHeight
      });
    }

    this.timeoutId = window.setTimeout(() => {
      this.setState({ skipInitialAnimation: false });
    }, 100);
  }

  componentDidUpdate(prevProps: Props): void {
    const { isExpanded, heightOverride } = this.props;

    if (prevProps.isExpanded !== isExpanded) {
      this.handleToggle();
    }

    if (heightOverride !== prevProps.heightOverride) {
      this.setState({
        contentHeight: heightOverride
      });
    }
  }

  componentWillUnmount() {
    if (this.timeoutId === null) {
      return;
    }

    clearTimeout(this.timeoutId);
    this.timeoutId = undefined;
  }

  toggleExpanded = (): void => {
    this.timeoutId = window.setTimeout(() => this.setState({
      isExpanded: Boolean(this.props.isExpanded)
    }), 10);
  };

  handleToggle = (): void => {
    const contentInfo = getElementInfo(this.contentRef.current);
    this.setState({
      contentHeight: contentInfo ? contentInfo.height : 0,
      unsetContentHeight: false
    }, this.toggleExpanded);
  };

  handleTransitionEnd = (evt: TransitionEvent<HTMLElement>): void => {
    const { isExpanded } = this.state;

    if (isExpanded && evt.propertyName === 'height') {
      this.setState({
        unsetContentHeight: true
      });
    }
  };

  render(): React.ReactNode {
    const { children, wrapperClassName, contentClassName } = this.props;
    const { isExpanded, contentHeight, unsetContentHeight, skipInitialAnimation } = this.state;

    const wrapperClasses = classnames('collapsible-content', wrapperClassName, {
      'collapsible-content--is-expanded': isExpanded,
      'collapsible-content--skip-animation': skipInitialAnimation
    });

    let height = isExpanded ? contentHeight : 0;
    if (isExpanded && (unsetContentHeight || skipInitialAnimation)) {
      // height is unset after the CSS animation ended, but only for expanded
      // so it will respond to all child elements height changes
      // height is unset also if we are initially expanded, so we can skip animation on first run
      height = undefined;
    }

    return (
      <div
        className={wrapperClasses}
        style={{ height }}
        onTransitionEnd={this.handleTransitionEnd}
      >
        <div
          className={contentClassName}
          ref={this.contentRef}
        >
          {children}
        </div>
      </div>
    );
  }
}

export default CollapsibleContainer;
